<template>
  <div class="auth-layout">
    <!-- Decorative circles -->
    <div class="decoration-circle circle-1" />
    <div class="decoration-circle circle-2" />
    <div class="decoration-circle circle-3" />

    <div class="auth-container">
      <!-- Auth card with rainbow border -->
      <div class="auth-card">
        <!-- Auth content -->
        <div class="auth-content indiefy-form">
          <router-view />
        </div>

        <!-- Footer -->
        <div class="auth-footer">
          <div class="language-selector">
            <div class="select is-small">
              <select
                v-model="currentLanguage"
                @change="handleLanguageChange"
              >
                <option value="en">
                  English
                </option>
                <option value="es">
                  Español
                </option>
                <option value="pt">
                  Português
                </option>
                <option value="tr">
                  Türkçe
                </option>
              </select>
            </div>
          </div>
          <div class="copyright">
            <small>&copy; {{ new Date().getFullYear() }} Indiefy. {{ $t('general.all-rights') }}</small>
          </div>
        </div>
      </div>

      <!-- Platform logos -->
      <div class="platforms-section">
        <h6 class="platforms-title">
          {{ $t('landing.trusted-by') }}
        </h6>
        <div class="platform-logos">
          <img
            src="@/assets/images/landing/spotify-logo.svg"
            alt="Spotify"
            class="platform-logo"
          >
          <img
            src="@/assets/images/landing/apple-music.svg"
            alt="Apple Music"
            class="platform-logo"
          >
          <img
            src="@/assets/images/landing/youtube-music.png"
            alt="YouTube Music"
            class="platform-logo youtube-logo"
          >
          <img
            src="@/assets/images/landing/amazon-music-logo.svg"
            alt="Amazon Music"
            class="platform-logo amazon-logo"
          >
          <img
            src="@/assets/images/landing/tiktok-logo.svg"
            alt="TikTok"
            class="platform-logo"
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
// AuthLayout is a wrapper component for all authentication related views
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';

const { locale } = useI18n();
const router = useRouter();
const route = useRoute();

// Initialize language from URL or default to current locale
const currentLanguage = ref(route.query.lang || locale.value);

// Handle language change
const handleLanguageChange = () => {
  // Update i18n locale
  locale.value = currentLanguage.value;

  // Update URL with new language
  router.replace({
    query: {
      ...route.query,
      lang: currentLanguage.value,
    },
  });
};

// Set initial language from URL on mount
onMounted(() => {
  if (route.query.lang) {
    locale.value = route.query.lang;
    currentLanguage.value = route.query.lang;
  }
});
</script>

<style lang="scss" scoped>
@import '@/styles/abstracts/colors';
@import '@/styles/abstracts/breakpoints';
@import '@/styles/abstracts/animations';

.youtube-logo {
  height: 30px !important;
  margin-top: -3px;
}

.amazon-logo {
  height: 30px !important;
}

.auth-layout {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-background, #f8fafc); /* Fallback color */
  background: linear-gradient(135deg, var(--color-background, #f8fafc) 0%, var(--color-background-lighter, #f1f5f9) 100%);
  position: relative;
  overflow: hidden;
  padding: 2rem 1rem;

  // Decorative circles
  .decoration-circle {
    position: absolute;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--color-primary, #{$indiefy-blue}) 0%, var(--color-secondary, #{$indiefy-lavender}) 100%);
    opacity: 0.1;

    &.circle-1 {
      width: 300px;
      height: 300px;
      top: -150px;
      left: -150px;
      animation: float 8s infinite ease-in-out;
    }

    &.circle-2 {
      width: 200px;
      height: 200px;
      top: calc(50% - 100px);
      right: -100px;
      animation: float 12s infinite ease-in-out reverse;
    }

    &.circle-3 {
      width: 150px;
      height: 150px;
      bottom: -50px;
      left: 20%;
      animation: float 10s infinite ease-in-out 2s;
    }
  }

  @media screen and (min-width: $tablet) {
    padding: 3rem;
  }
}

// Container for all authentication content
.auth-container {
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
}

// Styling for the authentication card
.auth-card {
  background-color: var(--color-card-background, #ffffff);
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  padding: 2rem;
  width: 100%;
  position: relative;
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden;
  backdrop-filter: blur(10px);

  // Rainbow border effect
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg,
      $indiefy-blue,
      $indiefy-lavender,
      $indiefy-pink,
      $indiefy-teal,
      $indiefy-blue
    );
    background-size: 400% 400%;
    animation: gradientShift 10s ease infinite;
  }

  @media screen and (min-width: $tablet) {
    padding: 2.5rem;
  }

  @media screen and (min-width: $desktop) {
    padding: 3rem;
  }

  .body--dark & {
    background-color: var(--color-card-background, #1e293b);
    border-color: rgba(255, 255, 255, 0.05);
  }
}

// Header with logo
.auth-header {
  text-align: center;
  margin-bottom: 2rem;

  .logo-container {
    position: relative;
    display: inline-block;
    padding: 0.5rem;
    border-radius: 8px;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);

      .logo-highlight {
        opacity: 1;
      }
    }

    &:active {
      transform: translateY(1px);
    }
  }

  .logo-highlight {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%);
    opacity: 0;
    transition: opacity 0.3s ease;
    border-radius: 8px;
    z-index: 1;
  }

  .auth-logo {
    max-width: 160px;
    height: auto;
    transition: transform 0.3s ease;
    position: relative;
    z-index: 2;
    display: block;

    .body--dark & {
      display: none;
    }

    .logo-container:hover & {
      transform: scale(1.05);
    }
  }

  .auth-logo-dark {
    max-width: 160px;
    height: auto;
    transition: transform 0.3s ease;
    position: relative;
    z-index: 2;
    display: none;

    .body--dark & {
      display: block;
    }

    .logo-container:hover & {
      transform: scale(1.05);
    }
  }
}

// Auth content - where the router view is displayed
.auth-content {
  position: relative;
  z-index: 2;
}

// Footer with language selector and copyright
.auth-footer {
  margin-top: 2.5rem;
  text-align: center;

  .language-selector {
    margin-bottom: 1rem;

    select {
      background-color: transparent;
      border: 1px solid rgba(0, 0, 0, 0.1);

      .body--dark & {
        border-color: rgba(255, 255, 255, 0.1);
        background-color: rgba(0, 0, 0, 0.2);
        color: white;
      }
    }
  }

  .copyright {
    small {
      color: var(--color-text-light, #64748b);
      font-size: 0.8rem;
    }
  }
}

// Platform logos section
.platforms-section {
  margin-top: 2.5rem;
  text-align: center;
  width: 100%;

  @media screen and (max-width: $mobile) {
    display: none;
  }

  .platforms-title {
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--color-text-light, #64748b);
    margin-bottom: 1rem;
  }

  .platform-logos {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 1.5rem;

    .platform-logo {
      height: 20px;
      width: auto;
      opacity: 0.5;
      transition: opacity 0.3s ease;
      filter: grayscale(100%);

      &:hover {
        opacity: 0.8;
        filter: grayscale(0%);
      }

      .body--dark & {
        filter: grayscale(100%) brightness(2);
      }
    }
  }
}

// Animations
@keyframes float {
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(5deg);
  }
}

@keyframes gradientShift {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}
</style>